/**
 * useTasksUsage — Hook para consumo da RPC `get_tenant_tasks_usage`
 *
 * Retorna o uso atual de tarefas (mensal) e de armazenamento ("Espaço de Arquivos")
 * do tenant ativo, agregando limites do plano base + add-ons (task_pack, task_storage).
 *
 * Padrão server-side: toda a lógica de cálculo (período em SP, soma de packs/storage,
 * leitura do bucket `tarefas`) acontece na função RPC. O frontend apenas consome.
 *
 * @version 1.1.0
 * RELEASE: 2026-04-20 v1.1.0 — Adiciona `active_pack_quantity` (modelo qty × unitário)
 * RELEASE: 2026-04-20 v1.0.0 — Criação inicial (Etapa 2 do módulo de cobrança de Tarefas)
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';

const logger = createLogger('useTasksUsage');

/**
 * Estrutura retornada pela RPC `get_tenant_tasks_usage`.
 * Os nomes seguem snake_case do Postgres (mantidos para evitar transformação extra).
 */
export interface TasksUsage {
  /** Quantidade de tarefas criadas no mês corrente (fuso America/Sao_Paulo) */
  tasks_created_month: number;
  /** Limite efetivo de tarefas/mês (plano base ou pack ativo — o maior) */
  tasks_limit: number;
  /** Quantas tarefas ainda podem ser criadas neste mês */
  tasks_remaining: number;
  /** % consumida (0-100) com 2 casas decimais */
  tasks_pct_used: number;

  /** Espaço já consumido no bucket `tarefas` (em MB) */
  storage_used_mb: number;
  /** Espaço total disponível (plano + 5GB do task_pack + 10GB × storage_extras) */
  storage_limit_mb: number;
  /** Espaço restante (limit - used), nunca negativo */
  storage_remaining_mb: number;
  /** % consumida do espaço (0-100) */
  storage_pct_used: number;

  /** UUID do tenant_addon ativo do tipo task_pack (consolidado em 1 linha por SKU) */
  active_pack_addon_id: string | null;
  /** Slug do task_pack ativo (ex: 'tasks_pack_1k') */
  active_pack_slug: string | null;
  /** Quantidade total de unidades de task_pack contratadas (qty da linha consolidada) */
  active_pack_quantity: number;
  /** Quantidade de add-ons de storage extra ativos (cada um = 10 GB) */
  active_storage_addons_qty: number;

  /** Início do período de contagem (timestamp ISO em SP) */
  period_start: string;
  /** Fim do período (timestamp ISO em SP) */
  period_end: string;
}

/**
 * Hook React Query para uso de tarefas + storage.
 *
 * @param tenantHint — opcional; se omitido usa o tenant ativo do contexto
 * @returns Query com `data` tipado como `TasksUsage`
 */
export function useTasksUsage(tenantHint?: string) {
  const { currentTenant } = useAuth();
  const tenantId = tenantHint ?? currentTenant?.id;

  return useQuery({
    queryKey: ['tasks-usage', tenantId],
    queryFn: async (): Promise<TasksUsage | null> => {
      if (!tenantId) return null;

      // A RPC retorna SETOF (TABLE) — pegamos a primeira linha.
      const { data, error } = await supabase.rpc('get_tenant_tasks_usage', {
        p_tenant_hint: tenantId,
      });

      if (error) {
        logger.error('Erro ao buscar uso de tarefas:', error.message);
        throw error;
      }

      const row = Array.isArray(data) ? data[0] : data;
      return (row ?? null) as TasksUsage | null;
    },
    enabled: !!tenantId,
    // Usage muda lentamente; 60s é suficiente e evita carga desnecessária no servidor.
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
  });
}
